import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BarChart3, TrendingUp, Users, FileText, DollarSign, Calendar, ArrowUp, ArrowDown, Download, Filter } from 'lucide-react';

const Analytics = () => {
  const [dateRange, setDateRange] = useState('30');
  const [chartData, setChartData] = useState(null);
  const lineChartRef = useRef(null);
  const pieChartRef = useRef(null);
  const barChartRef = useRef(null);
  const chartInstances = useRef([]);

  useEffect(() => {
    loadChartJS();
    return () => {
      // Cleanup charts on unmount
      chartInstances.current.forEach(chart => chart.destroy());
    };
  }, []);

  useEffect(() => {
    if (window.Chart && dateRange) {
      initializeCharts();
    }
  }, [dateRange]);

  const loadChartJS = () => {
    if (!window.Chart) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      script.onload = () => initializeCharts();
      document.head.appendChild(script);
    } else {
      initializeCharts();
    }
  };

  const initializeCharts = () => {
    // Destroy existing charts
    chartInstances.current.forEach(chart => chart.destroy());
    chartInstances.current = [];

    // Line Chart - Referrals Over Time
    if (lineChartRef.current) {
      const ctx = lineChartRef.current.getContext('2d');
      const lineChart = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
          datasets: [
            {
              label: 'Referrals Submitted',
              data: [12, 19, 15, 25],
              borderColor: '#D4AF37',
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
              tension: 0.4,
              fill: true
            },
            {
              label: 'Completed',
              data: [8, 14, 12, 20],
              borderColor: '#10b981',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              tension: 0.4,
              fill: true
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom'
            }
          },
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      });
      chartInstances.current.push(lineChart);
    }

    // Pie Chart - Status Distribution
    if (pieChartRef.current) {
      const ctx = pieChartRef.current.getContext('2d');
      const pieChart = new window.Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Pending', 'In Progress', 'Scheduled', 'Completed'],
          datasets: [{
            data: [15, 25, 30, 50],
            backgroundColor: [
              '#fbbf24',
              '#3b82f6',
              '#8b5cf6',
              '#10b981'
            ]
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom'
            }
          }
        }
      });
      chartInstances.current.push(pieChart);
    }

    // Bar Chart - Top Providers
    if (barChartRef.current) {
      const ctx = barChartRef.current.getContext('2d');
      const barChart = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['Dr. Johnson', 'Dr. Chen', 'Dr. Martinez', 'Dr. Davis', 'Dr. Wilson'],
          datasets: [{
            label: 'Referrals',
            data: [45, 38, 32, 28, 24],
            backgroundColor: '#D4AF37'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      });
      chartInstances.current.push(barChart);
    }
  };

  const handleExport = () => {
    alert('Exporting analytics report...');
  };
  const stats = [
    { label: 'Total Referrals', value: '1,247', change: '+12.5%', trend: 'up' },
    { label: 'Conversion Rate', value: '78.3%', change: '+5.2%', trend: 'up' },
    { label: 'Avg. Response Time', value: '2.4 hrs', change: '-15%', trend: 'up' },
    { label: 'Revenue Generated', value: '$487K', change: '+23.1%', trend: 'up' }
  ];

  const topProviders = [
    { name: 'Dr. Gerard Nikodem', referrals: 237, revenue: '$94,800' },
    { name: 'Dr. Monya Phung', referrals: 174, revenue: '$69,600' },
    { name: 'Dr. David Schertzer', referrals: 158, revenue: '$63,200' },
    { name: 'Dr. Justin Garner', referrals: 114, revenue: '$45,600' },
    { name: 'Dr. Elizabeth Fassler', referrals: 100, revenue: '$40,000' }
  ];

  const monthlyData = [
    { month: 'Jan', referrals: 89, completed: 71 },
    { month: 'Feb', referrals: 95, completed: 76 },
    { month: 'Mar', referrals: 103, completed: 84 },
    { month: 'Apr', referrals: 98, completed: 79 },
    { month: 'May', referrals: 112, completed: 91 },
    { month: 'Jun', referrals: 108, completed: 87 },
    { month: 'Jul', referrals: 121, completed: 98 },
    { month: 'Aug', referrals: 115, completed: 93 },
    { month: 'Sep', referrals: 128, completed: 104 },
    { month: 'Oct', referrals: 134, completed: 108 },
    { month: 'Nov', referrals: 144, completed: 116 }
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2">Analytics Dashboard</h1>
        <p className="text-gray-600">Comprehensive insights into your referral network performance</p>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">{stat.label}</p>
                {stat.trend === 'up' ? (
                  <ArrowUp className="h-4 w-4 text-green-500" />
                ) : (
                  <ArrowDown className="h-4 w-4 text-red-500" />
                )}
              </div>
              <p className="text-3xl font-bold text-[#0A1628] mb-1">{stat.value}</p>
              <p className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change} from last month
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Date Range and Export */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Date Range:</label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="365">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
        <Button onClick={handleExport} className="bg-[#0A1628] text-[#D4AF37] hover:bg-[#1a2332]">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Chart Visualizations */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Line Chart - Referrals Over Time */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Referrals Over Time</CardTitle>
            <CardDescription>Track referral submission and completion trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: '300px' }}>
              <canvas ref={lineChartRef}></canvas>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart - Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Status Distribution</CardTitle>
            <CardDescription>Current referral status breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: '300px' }}>
              <canvas ref={pieChartRef}></canvas>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bar Chart - Top Providers */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Top Referring Providers</CardTitle>
          <CardDescription>Providers with highest referral volume</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ height: '300px' }}>
            <canvas ref={barChartRef}></canvas>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Monthly Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Referral Trends</CardTitle>
            <CardDescription>Monthly referral volume and completion rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {monthlyData.slice(-6).map((month, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="font-medium">{month.month}</span>
                    <span className="text-gray-600">
                      {month.completed}/{month.referrals} completed ({Math.round(month.completed/month.referrals*100)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-[#D4AF37] h-2.5 rounded-full"
                      style={{ width: `${(month.completed/month.referrals)*100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Referring Providers */}
        <Card>
          <CardHeader>
            <CardTitle>Top Referring Providers</CardTitle>
            <CardDescription>Highest volume providers this year</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topProviders.map((provider, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[#0A1628] rounded-full flex items-center justify-center text-[#D4AF37] font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-[#0A1628]">{provider.name}</p>
                      <p className="text-sm text-gray-600">{provider.referrals} referrals</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#D4AF37]">{provider.revenue}</p>
                    <p className="text-xs text-gray-600">revenue</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-[#D4AF37]" />
              Referral by Specialty
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">General Dentistry</span>
                <span className="font-semibold text-[#0A1628]">847</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Orthodontics</span>
                <span className="font-semibold text-[#0A1628]">245</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Periodontics</span>
                <span className="font-semibold text-[#0A1628]">89</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Endodontics</span>
                <span className="font-semibold text-[#0A1628]">66</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-[#D4AF37]" />
              Avg. Time to Completion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Wisdom Teeth</span>
                <span className="font-semibold text-[#0A1628]">5.2 days</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Dental Implants</span>
                <span className="font-semibold text-[#0A1628]">8.7 days</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Bone Grafting</span>
                <span className="font-semibold text-[#0A1628]">12.4 days</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">TMJ Treatment</span>
                <span className="font-semibold text-[#0A1628]">6.1 days</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-[#D4AF37]" />
              Growth Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">YoY Growth</span>
                <span className="font-semibold text-green-600">+34.2%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">New Providers</span>
                <span className="font-semibold text-[#0A1628]">12</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Retention Rate</span>
                <span className="font-semibold text-green-600">94.8%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Satisfaction</span>
                <span className="font-semibold text-[#0A1628]">4.8/5.0</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
